import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import moment from "moment";
import PropTypes from "prop-types";
import React from "react";
import Col from "react-bootstrap/Col";
import Dropdown from "react-bootstrap/Dropdown";
import DropdownButton from "react-bootstrap/DropdownButton";
import FormCheck from "react-bootstrap/FormCheck";
import FormControl from "react-bootstrap/FormControl";
import FormGroup from "react-bootstrap/FormGroup";
import FormLabel from "react-bootstrap/FormLabel";
import FormText from "react-bootstrap/FormText";
import Row from "react-bootstrap/Row";
import Datetime from "react-datetime";
import Select from "react-select";
import { Collapsible } from "ui/collapsible/collapsible";
import * as QS from "util/query-string";
import { sortByNameAsc } from "util/sort-by-prop";

import {
  DATE_PRESET_TODAY,
  DATE_PRESET_YESTERDAY,
  FILTER_DEBOUNCE,
  FILTER_TYPE_DATE_RANGE,
  FILTER_TYPE_MULTI_SELECT,
  FILTER_TYPE_SELECT,
  FILTER_TYPE_STRING,
  SENDER_INIT,
  SENDER_USER,
  SERIALIZED_INVERT_MATCH_KEY,
} from "./constants";
import { FilterApi } from "./filter-api";
import { FilterState } from "./filter-state";

// Date range values as string eg '2018-07-08T00:00:00.000Z'
// because moment objects fail equality checks
const DatePropType = PropTypes.shape({
  startDate: PropTypes.string,
  endDate: PropTypes.string,
});
const DatePresetPropType = PropTypes.shape({
  action: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  unit: PropTypes.string.isRequired,
});
const DatePresetOptionPropType = PropTypes.shape({
  label: PropTypes.string.isRequired,
  startDate: DatePresetPropType,
  endDate: DatePresetPropType,
});
const OptionPropType = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
});
const OptionsPropType = PropTypes.arrayOf(OptionPropType);
const DatePresetOptionsPropType = PropTypes.arrayOf(DatePresetOptionPropType);

/*
 * Dynamic, config-driven, general purpose filter component with integrations
 * with router history for navigation of state.
 *
 * Because of said integrations, all values must be serializable. This means
 * all comparisons will be done as string.
 *
 * Filter can be driven by filterState prop, or location prop; both
 * optional.
 *
 * Component provides an api that can be passed back to parent on init.
 * Component also fires a debounced onFilterChange callback after each
 * filter change.
 *
 */
class Filter extends React.Component {
  static get propTypes() {
    return {
      // We can initialize our filter criteria with filterState.
      filterState: PropTypes.shape({
        activeFilters: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            value: PropTypes.any,
          }),
        ),
        // Whether or not we invert the filter matches
        invertMatches: PropTypes.bool,
        // Used downstream to determine whether to push history or not.
        sender: PropTypes.string,
      }),
      // Whether or not the filter criteria can be inverted.
      invertible: PropTypes.bool,
      // We can initialize our filter criteria with location.search.
      location: PropTypes.object,
      /*
       * Dynamic filter field definitions. Be careful returning new
       * references that will break react life-cycle checks, resulting in
       * re-rendering and inconsistent behavior (like filter clearing).
       *
       * For example, DO NOT return a new function for getRecordValue as
       * this will break equality checks. Instead return a reference
       * to a function instance rather than a new function instance.
       *
       * Do this:
       * {
       *   getRecordValue: someAccessorFunction,
       * }
       *
       * NOT this:
       * {
       *   getRecordValue: (option) => option.id,
       * }
       *
       * */
      fields: PropTypes.arrayOf(
        PropTypes.shape({
          getRecordValue: PropTypes.func,
          label: PropTypes.string,
          name: PropTypes.string,
          helpText: PropTypes.string,
          initialValue: PropTypes.oneOfType([
            PropTypes.string,
            DatePropType,
            OptionsPropType,
          ]),
          type: PropTypes.oneOf([
            FILTER_TYPE_DATE_RANGE,
            FILTER_TYPE_STRING,
            FILTER_TYPE_SELECT,
            FILTER_TYPE_MULTI_SELECT,
          ]),
          // Options for select types. Be careful updating options as it
          // can trigger lifecycle re-renders.
          options: PropTypes.oneOfType([
            OptionsPropType,
            DatePresetOptionsPropType,
          ]),
          placeholder: PropTypes.oneOfType([PropTypes.string, DatePropType]),
          showPlaceholder: PropTypes.oneOfType([PropTypes.bool, DatePropType]),
        }),
      ).isRequired,
      getApi: PropTypes.func,
      onFilterChange: PropTypes.func.isRequired,
    };
  }

  static get defaultProps() {
    return {
      invertible: false,
    };
  }

  /*
   * Get filter default values. Although we require string values,
   * the select widgets use null to indicate no selection.
   */
  static _getFilterDefaultValue(filter) {
    switch (filter.type) {
      case FILTER_TYPE_DATE_RANGE:
        return {
          startDate: null,
          endDate: null,
        };
      case FILTER_TYPE_SELECT:
        return null;
      case FILTER_TYPE_MULTI_SELECT:
        return null;
      default:
        return "";
    }
  }

  /*
   * Determine if a record matches the filter criteria.
   */
  static _filterDateRange(filterValue, rawRecordValue) {
    if (!rawRecordValue) {
      return false;
    }

    const recordValue = moment.isMoment(rawRecordValue)
      ? rawRecordValue
      : moment.utc(rawRecordValue);

    if (filterValue.startDate && filterValue.startDate > recordValue) {
      return false;
    }

    return !(filterValue.endDate && filterValue.endDate < recordValue);
  }

  /*
   * Determine if a record matches the filter criteria.
   */
  static _filterString(filterValue, recordValue) {
    return recordValue.toLowerCase().includes(filterValue.toLowerCase());
  }

  /*
   * Determine if a record matches the filter criteria.
   */
  static _filterSelect(getRecordValue, filterValue, recordValue) {
    return String(getRecordValue(recordValue)) === String(filterValue);
  }

  /*
   * Determine if a record matches the filter criteria.
   */
  static _filterMultiSelect(getRecordValue, filterValues, recordValues) {
    return filterValues.every((selected) => {
      const recValues = Array.isArray(recordValues)
        ? recordValues
        : [recordValues];
      return recValues
        .map(getRecordValue)
        .map((rv) => String(rv))
        .includes(String(selected.value));
    });
  }

  /*
   * Check a model representation for match against filter criteria.
   */
  static applyFilters(activeFilters, invertMatches, modelObj) {
    const match = activeFilters.every((filter) => {
      const fieldValue = modelObj[filter.name];

      return filter.checkFilterMatch(fieldValue);
    });

    return invertMatches ? !match : match;
  }

  constructor(props) {
    super(props);

    this.serializeFilters = this.serializeFilters.bind(this);

    const filterState = this._getFilterStateFromProps(props);

    // Used to bootstrap initialValues on initial render.
    this.initialized = false;

    this.state = {
      filters: this._initFilterState(props.fields, filterState.activeFilters),
      lastFilterState: filterState.serializable,
      invertMatches: filterState.invertMatches,
      sender: SENDER_INIT,
      initialized: false,
    };

    this._debouncedOnFilterChange = debounce(
      props.onFilterChange,
      FILTER_DEBOUNCE,
    );

    this._getFilterInitialValue = this._getFilterInitialValue.bind(this);
    this._onInvertMatchChange = this._onInvertMatchChange.bind(this);
    this._renderFormField = this._renderFormField.bind(this);
    this._renderInput = this._renderInput.bind(this);
    this.clearFilters = this.clearFilters.bind(this);
    this.getFilteredData = this.getFilteredData.bind(this);
    this.getFilterState = this.getFilterState.bind(this);

    this.getApi(props.getApi);

    this._clearButtonClass = "form-control-clear fa fa-times pull-right";
  }

  componentDidUpdate(prevProps, prevState) {
    const { fields } = this.props;
    const oldFields = prevProps.fields;

    // Watch for field definition changes.
    const fieldsChanged = !isEqual(fields, oldFields);
    if (fieldsChanged) {
      // When field definitions change we need to send SENDER_INIT to tell the
      // consumer that the filter has re-initialized.
      const filterState = this.getFilterState(this.state, this.props);

      this.setState({
        filters: this._initFilterState(fields, filterState.activeFilters),
        invertMatches: filterState.invertMatches,
        sender: SENDER_INIT,
      });
    }

    // Watch for location changes.
    const locationChanged = this.props.location !== prevProps.location;
    if (locationChanged) {
      const filterState = this._getFilterStateFromProps(this.props);
      const curFilterState = this.getFilterState(this.state, this.props);

      if (!curFilterState.isEqual(filterState)) {
        this.setState({
          filters: this._initFilterState(fields, filterState.activeFilters),
          invertMatches: filterState.invertMatches,
          sender: SENDER_INIT,
        });
      }
    }

    const { lastFilterState } = this.state;
    const curFilterState = this.getFilterState(this.state, this.props);

    // Watch for user input filter changes.
    const filterStateChanged = !FilterState.isEqualSerialized(
      lastFilterState,
      curFilterState.serializable,
    );

    if (filterStateChanged) {
      this.setState({ lastFilterState: curFilterState.serializable }, () =>
        this._debouncedOnFilterChange(curFilterState),
      );
    }
  }

  componentDidMount() {
    // This makes sure we only read initialValue ONE TIME during
    // initialization.
    if (!this.initialized) {
      this.initialized = true;
      const filterState = this.getFilterState(this.state, this.props);
      filterState.sender = SENDER_INIT;
      this.props.onFilterChange(filterState);
    }
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    // Watch for filterState changes.
    const { lastFilterState } = this.state;
    const nextFilterState = this.getFilterState(nextState, nextProps);
    const filterStateChanged = !FilterState.isEqualSerialized(
      lastFilterState,
      nextFilterState.serializable,
    );

    if (filterStateChanged) {
      return true;
    }

    // Watch for location changes.
    const locationChanged = this.props.location !== nextProps.location;
    if (locationChanged) {
      return true;
    }

    // Watch for field definition changes.
    const { fields } = nextProps;
    const oldFields = this.props.fields;
    const fieldsChanged = !isEqual(fields, oldFields);
    if (fieldsChanged) {
      return true;
    }

    return false;
  }

  /*
   * Get an array of objects representing the active filter criteria.
   */
  _getActiveFilters(filters) {
    return Object.values(filters)
      .filter((f) => f.checkFilterMatch)
      .map(this._getActiveFilterMapper)
      .sort(sortByNameAsc);
  }

  /*
   * Map a field instance to an activeFilter instance.
   *
   * @param {field} field config instance.
   * @returns {activeFilter} activeFilter instance.
   */
  _getActiveFilterMapper(field) {
    return {
      checkFilterMatch: field.checkFilterMatch,
      name: field.name,
      value: field.value,
    };
  }

  _getFilterInitialValue(filter) {
    if (!this.initialized && filter.initialValue !== undefined) {
      if (filter.type === FILTER_TYPE_DATE_RANGE) {
        const startDate = filter.initialValue.startDate;
        const endDate = filter.initialValue.endDate;

        return {
          startDate: startDate ? moment.utc(startDate) : null,
          endDate: endDate ? moment.utc(endDate) : null,
        };
      }

      return filter.initialValue;
    }

    return this.constructor._getFilterDefaultValue(filter);
  }

  /*
   * Get filterState from props.filterState or by deserializing
   * props.location.search. This allows us to reinitialize the filters
   * from props.
   *
   * @param props
   * @returns {{activeFilters, [p: string]: *}}
   * @private
   */
  _getFilterStateFromProps(props) {
    const { search } = props.location || {};

    const filterState = this.deserializeFilters(props.fields, search);
    if (filterState) {
      return filterState;
    }

    const { activeFilters, invertMatches, sender } = props.filterState || {};

    return new FilterState(props.fields, {
      activeFilters,
      invertMatches,
      sender,
    });
  }

  /*
   * API: Retrieve an object representing the current filter state.
   *
   * @param {Object} [state] - Optional state object. Can be used to query
   *   last state when provided or current state when omitted.
   *
   * @param {Object} [props] - Optional props object. It is used when the fields
   *   in props should be associated with a FilterState.
   *
   * @returns {FilterState} - A FilterState instance.
   */
  getFilterState(state, props = {}) {
    if (!state) {
      state = this.state;
    }

    const { fields } = props;
    const { filters, invertMatches, sender } = state;

    const activeFilters = this._getActiveFilters(filters);

    return new FilterState(fields, { activeFilters, invertMatches, sender });
  }

  /*
   * Generates a new filter object. If filterState is provided we load
   * the new filter object to match the specified filterState.
   *
   * @param field
   * @param filterState
   * @returns {*}
   * @private
   */
  _getNewFilter(field, filterState = {}) {
    const {
      _filterDateRange,
      _filterSelect,
      _filterMultiSelect,
      _filterString,
    } = this.constructor;
    const filter = Object.assign({}, field);

    filter.getRecordValue =
      typeof filter.getRecordValue === "function"
        ? filter.getRecordValue
        : (el) => el;

    if (filterState.value === undefined) {
      filter.value = this._getFilterInitialValue(filter);
    } else {
      filter.value = filterState.value;
    }

    if (filter.type === FILTER_TYPE_DATE_RANGE) {
      if (filter.showPlaceholder === undefined) {
        filter.showPlaceholder = {};
      }

      if (filter.showPlaceholder.startDate === undefined) {
        filter.showPlaceholder.startDate = true;
      }

      if (filter.showPlaceholder.endDate === undefined) {
        filter.showPlaceholder.endDate = true;
      }

      if (!filter.value.startDate && !filter.value.endDate) {
        filter.checkFilterMatch = null;

        return filter;
      }
    } else {
      if (filter.showPlaceholder === undefined) {
        filter.showPlaceholder = true;
      }

      if (!filter.value) {
        filter.checkFilterMatch = null;

        return filter;
      }
    }

    switch (filter.type) {
      case FILTER_TYPE_DATE_RANGE:
        filter.checkFilterMatch = _filterDateRange.bind(null, filter.value);
        break;
      case FILTER_TYPE_SELECT:
        filter.checkFilterMatch = _filterSelect.bind(
          null,
          filter.getRecordValue,
          filter.value,
        );
        break;
      case FILTER_TYPE_MULTI_SELECT:
        filter.checkFilterMatch = _filterMultiSelect.bind(
          null,
          filter.getRecordValue,
          filter.value,
        );
        break;
      default:
        filter.checkFilterMatch = _filterString.bind(null, filter.value);
        break;
    }

    return filter;
  }

  _initFilterState(fieldDefs, filterStates = []) {
    const filters = {};
    const filterStateMap = {};
    filterStates.forEach((fs) => {
      filterStateMap[fs.name] = fs;
    });

    fieldDefs.forEach((field) => {
      const filterState = filterStateMap[field.name];
      const filter = this._getNewFilter(field, filterState);

      filters[filter.name] = filter;
    });

    return filters;
  }

  _onDateRangePresetChange(filter, option) {
    const { _filterDateRange } = this.constructor;
    const { filters } = this.state;
    const localNow = moment();
    const localYesterday = localNow.clone().subtract(1, "days");

    const newFilter = Object.assign({}, filter);

    newFilter.value = {
      startDate: null,
      endDate: null,
    };

    if (option.value) {
      // Special presets
      switch (option.value) {
        case DATE_PRESET_TODAY.value:
          newFilter.value.startDate = moment.utc(
            localNow.clone().startOf("day"),
          );
          newFilter.value.endDate = moment.utc(localNow.clone().endOf("day"));
          break;
        case DATE_PRESET_YESTERDAY.value:
          newFilter.value.startDate = moment.utc(
            localYesterday.clone().startOf("day"),
          );
          newFilter.value.endDate = moment.utc(
            localYesterday.clone().endOf("day"),
          );
          break;
        default:
          console.warn(`${option.value} date preset does not exist.`);
          break;
      }
    } else {
      // Dynamic presets
      const startOps = option.startDate;
      const endOps = option.endDate;

      if (startOps) {
        const localStart = localNow
          .clone()
          [startOps.action](startOps.value, startOps.unit);
        newFilter.value.startDate = moment.utc(localStart);
      }

      if (endOps) {
        const localEnd = localNow
          .clone()
          [endOps.action](endOps.value, endOps.unit);
        newFilter.value.endDate = moment.utc(localEnd);
      }
    }

    newFilter.checkFilterMatch =
      newFilter.value.startDate || newFilter.value.endDate
        ? _filterDateRange.bind(null, Object.assign({}, newFilter.value))
        : null;

    this.setState({
      filters: {
        ...filters,
        [filter.name]: newFilter,
      },
      sender: SENDER_USER,
    });
  }

  _onDateRangeChange(filter, key, value) {
    const { _filterDateRange } = this.constructor;
    const { filters } = this.state;

    const newFilter = cloneDeep(filter);

    if (!newFilter.value) {
      newFilter.value = {
        startDate: null,
        endDate: null,
      };
    }

    newFilter.value[key] = value;

    newFilter.checkFilterMatch =
      newFilter.value.startDate || newFilter.value.endDate
        ? _filterDateRange.bind(null, Object.assign({}, newFilter.value))
        : null;

    this.setState({
      filters: {
        ...filters,
        [filter.name]: newFilter,
      },
      sender: SENDER_USER,
    });
  }

  _onInputChange(filter, e) {
    const { _filterString } = this.constructor;
    const { value } = e.target;
    const { filters } = this.state;

    const newFilter = Object.assign({}, filter);

    newFilter.value = value;
    newFilter.checkFilterMatch = value ? _filterString.bind(null, value) : null;

    this.setState({
      filters: {
        ...filters,
        [filter.name]: newFilter,
      },
      sender: SENDER_USER,
    });
  }

  _onInvertMatchChange(e) {
    this.setState({
      invertMatches: !e.currentTarget.checked,
      sender: SENDER_USER,
    });
  }

  _onSelectChange(filter, selected) {
    const { _filterMultiSelect, _filterSelect } = this.constructor;
    const { filters } = this.state;

    const newFilter = Object.assign({}, filter);

    if (selected === null) {
      newFilter.value = null;
      newFilter.checkFilterMatch = null;
    } else if (filter.type === FILTER_TYPE_SELECT) {
      // Single Select
      newFilter.value = selected.value;
      newFilter.checkFilterMatch = _filterSelect.bind(
        null,
        filter.getRecordValue,
        selected.value,
      );
    } else if (
      filter.type === FILTER_TYPE_MULTI_SELECT &&
      selected.length // Null check handled above!
    ) {
      // Multi Select
      newFilter.value = selected;
      newFilter.checkFilterMatch = _filterMultiSelect.bind(
        null,
        filter.getRecordValue,
        selected,
      );
    } else {
      newFilter.value = null;
      newFilter.checkFilterMatch = null;
    }

    this.setState({
      filters: {
        ...filters,
        [filter.name]: newFilter,
      },
      sender: SENDER_USER,
    });
  }

  _renderInput(filter) {
    const { helpText, label, name, placeholder, showPlaceholder, value } =
      filter;

    return (
      <FormGroup key={name}>
        <FormLabel>{label}</FormLabel>
        <FormControl
          name={name}
          value={value}
          onChange={this._onInputChange.bind(this, filter)}
          placeholder={showPlaceholder && placeholder}
        />
        {helpText && <FormText>{helpText}</FormText>}
      </FormGroup>
    );
  }

  _renderSelect(filter) {
    const {
      helpText,
      label,
      name,
      options,
      placeholder,
      showPlaceholder,
      type,
      value,
    } = filter;

    const selected =
      type === FILTER_TYPE_SELECT
        ? options.find((o) => o.value === value) || null // Single
        : value; // Multi

    return (
      <FormGroup key={name}>
        <FormLabel>{label}</FormLabel>
        <Select
          name={name}
          isMulti={type === FILTER_TYPE_MULTI_SELECT}
          onChange={this._onSelectChange.bind(this, filter)}
          options={options}
          placeholder={showPlaceholder && placeholder}
          value={selected}
          isClearable
        />
        {helpText && <FormText>{helpText}</FormText>}
      </FormGroup>
    );
  }

  _renderDateRangeOptions(filter) {
    const { options } = filter;

    const predefinedTitle = (
      <i className="fa fa-calendar-o" aria-hidden="true" />
    );

    const preDefined = options.map((o, i) => {
      if (o === null) {
        return <Dropdown.Item key={i} divider />;
      }

      return (
        <Dropdown.Item
          key={i}
          onClick={this._onDateRangePresetChange.bind(this, filter, o)}
        >
          {o.label}
        </Dropdown.Item>
      );
    });

    return (
      <div className="predefined-dates">
        <DropdownButton
          title={predefinedTitle}
          id="predefined-dates"
          variant="secondary"
        >
          {preDefined}
        </DropdownButton>
      </div>
    );
  }

  _renderDateRangeSelect(filter) {
    const {
      helpText,
      label,
      name,
      options,
      placeholder,
      showPlaceholder,
      value,
    } = filter;

    const colWidth = options ? 11 : 12;
    const startDate = value.startDate;
    const endDate = value.endDate;

    value.startDate = moment.isMoment(startDate)
      ? startDate.clone().local()
      : null;

    value.endDate = moment.isMoment(endDate) ? endDate.clone().local() : null;

    const startProps = {
      placeholder: showPlaceholder.startDate ? placeholder.startDate : "",
    };
    const endProps = {
      placeholder: showPlaceholder.endDate ? placeholder.endDate : "",
    };

    return (
      <FormGroup key={name}>
        <FormLabel>{label}</FormLabel>
        <Row>
          {options && this._renderDateRangeOptions(filter)}
          <Col xs={colWidth}>
            <FormLabel className="date-field-label start-date">
              From date
            </FormLabel>
            <Datetime
              className="date-field start-date"
              onChange={this._onDateRangeChange.bind(this, filter, "startDate")}
              inputProps={startProps}
              timeFormat={true}
              value={value.startDate}
            />
            <FormLabel className="date-field-label end-date">To date</FormLabel>
            <Datetime
              className="date-field end-date"
              onChange={this._onDateRangeChange.bind(this, filter, "endDate")}
              timeFormat={true}
              inputProps={endProps}
              value={value.endDate}
            />
          </Col>
        </Row>
        {helpText && <FormText>{helpText}</FormText>}
      </FormGroup>
    );
  }

  _renderFormField(fieldName) {
    const filter = this.state.filters[fieldName];

    if (!filter) {
      console.error(`${fieldName} filter does not exist!`);

      return null;
    }

    switch (filter.type) {
      case FILTER_TYPE_DATE_RANGE:
        return this._renderDateRangeSelect(filter);
      case FILTER_TYPE_SELECT:
      case FILTER_TYPE_MULTI_SELECT:
        return this._renderSelect(filter);
      default:
        return this._renderInput(filter);
    }
  }

  _renderInvertMatchCheckbox() {
    return (
      <FormGroup>
        <FormCheck
          checked={!this.state.invertMatches}
          label="Show only items matching the filter rules"
          onChange={this._onInvertMatchChange}
          type="checkbox"
        />
        <FormText>
          When checked, only items matching the filters will get displayed.
          Uncheck to exclude matching items.
        </FormText>
      </FormGroup>
    );
  }

  _renderForm() {
    const { fields } = this.props;

    return fields.map((f) => this._renderFormField(f.name));
  }

  clearFilters(e) {
    if (e) {
      e.stopPropagation();
    }

    const { fields } = this.props;
    const filterState = new FilterState(fields, { sender: SENDER_USER });

    // DO NOT CALL onFilterStateChange. This happens
    // automatically via componentDidUpdate.
    this.setState({
      filters: this._initFilterState(fields, filterState.activeFilters),
      invertMatches: filterState.invertMatches,
      sender: filterState.sender,
    });
  }

  /*
   * API: Deserialize a query string to filterState.
   */
  deserializeFilters(fieldDefs, search) {
    if (!search) {
      return null;
    }

    const params = QS.parse(search);
    const activeFilters = [];
    const invertMatches = params[SERIALIZED_INVERT_MATCH_KEY] === "true";

    Object.entries(params).forEach(([name, value]) => {
      const field = fieldDefs.find((f) => f.name === name);
      if (!field) {
        // Param does not exist in filter configuration. Skip.
        return;
      }

      const activeFilter = { name, value };

      if (field.type === FILTER_TYPE_MULTI_SELECT) {
        const rawValues = Array.isArray(value) ? value : value.split(",");
        const opts = field.options || [];
        const optMap = opts.reduce((acc, o) => {
          acc[o.value] = o;

          return acc;
        }, {});

        activeFilter.value = rawValues.map((v) => {
          return optMap[v] || { label: v, value: v };
        });
      }

      activeFilters.push(activeFilter);
    });

    return new FilterState(fieldDefs, {
      activeFilters,
      invertMatches,
      sender: SENDER_INIT,
    });
  }

  /*
   * API: Serialize filterState to a query string.
   */
  serializeFilters(filterState) {
    const { location = {} } = this.props;
    const params = QS.parse(location.search);

    // Add active params.
    filterState.activeFilters.forEach((f) => {
      if (Array.isArray(f.value)) {
        params[f.name] = f.value.map((v) => v.value);
      } else {
        params[f.name] = f.value;
      }
    });

    params[SERIALIZED_INVERT_MATCH_KEY] = filterState.invertMatches;

    // Reset page to 1 if exists.
    if ("page" in params) {
      params.page = 1;
    }

    // Remove inactive params.
    filterState.inactiveFilters.forEach((name) => {
      delete params[name];
    });

    return QS.stringify(params);
  }

  /**
   * Callback from parent to receive filter api instance.
   *
   * @callback filterApiCallback
   * @param {FilterApi} - Filter api instance.
   */

  /**
   * Retrieve the Filter api.
   *
   * @param {filterApiCallback} callback - Callback to receive filter api
   *   instance.
   * @return {FilterApi} - Filter api instance.
   */
  getApi(callback) {
    const filterApi = new FilterApi(this);

    if (typeof callback === "function") {
      callback(filterApi);
    }

    return filterApi;
  }

  /**
   * API: Apply the filters to retrieve the filtered data set matching filter
   * criteria.
   *
   * @param {Object[]} objects - POJO: an array of objects.
   * @return {Object[]} - Returns a filtered array
   */
  getFilteredData(objects) {
    const { applyFilters } = this.constructor;
    const filterState = this.getFilterState(this.state, this.props);
    const { activeFilters } = filterState;

    const boundFilter = applyFilters.bind(
      null,
      activeFilters,
      filterState.invertMatches,
    );

    return objects.filter(boundFilter);
  }

  getLabel() {
    const filters = this.getFilterState(this.state, this.props);
    const { activeFilterCount, invertMatches, isFiltered } = filters;
    const inverted = invertMatches ? "i" : "";
    const filterCount = activeFilterCount ? activeFilterCount : "";
    const hide = isFiltered ? "" : "hidden";

    return (
      <span>
        Filters{hide ? null : ` (${filterCount}${inverted})`}
        <span
          className={`${this._clearButtonClass} ${hide}`}
          onClick={this.clearFilters}
        />
      </span>
    );
  }

  render() {
    const { invertible } = this.props;
    const label = this.getLabel();

    return (
      <Collapsible className="ui-filter" label={label}>
        {invertible && this._renderInvertMatchCheckbox()}
        {this._renderForm()}
      </Collapsible>
    );
  }
}

export { Filter };
